import React, { useEffect, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DownloadIcon from '@mui/icons-material/Download'
import { useSnackbar } from 'notistack'
import { apiConstants, postGeneric } from '../../../common/constants'
import { formatDate } from '../../../helpers/UtilityHelper'
import { secondaryBtnSx } from '../../../styles/patterns'

const POLL_INTERVAL_MS = 10000
const MAX_POLLS = 60 // ~10 minutes

// Downloads the "Forward Shipment MIS (D2C)" report - the same async report the Reports page offers -
// for the date range the Forward Shipments page is showing. The report is built on the server, so this
// asks for it and polls until the file link is ready. Only the date range is sent: the report does not
// take the page's other filters (status, AWB, payment mode...). The report picks shipments by the date
// the AWB was created, so the messages say so.
export default function ForwardMisDownloadButton({ merchantId, fromDate, toDate, disabled = false }) {
  const { enqueueSnackbar } = useSnackbar()
  const [downloading, setDownloading] = useState(false)
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])

  const missingRange = !fromDate || !toDate
  const rangeText = missingRange ? '' : `${formatDate(fromDate, 'dd MMM yyyy')} to ${formatDate(toDate, 'dd MMM yyyy')}`

  const download = async () => {
    if (missingRange) return
    setDownloading(true)
    enqueueSnackbar(`Preparing the Forward Shipment MIS for shipments created from ${rangeText}. It will download when ready.`, { variant: 'info' })
    const request = {
      reportName: 'MIS_FWD',
      fromDate: formatDate(fromDate, 'yyyy-MM-dd'),
      toDate: formatDate(toDate, 'yyyy-MM-dd'),
      merchantRef: merchantId,
      overwrite: true, // first call builds a fresh file; the polls below must not rebuild it
    }
    try {
      for (let attempt = 0; attempt < MAX_POLLS && mounted.current; attempt += 1) {
        const { data } = await postGeneric(apiConstants.CREATE_REPORT_ASYNC, request)
        delete request.overwrite
        if (data.requsetState === 'COMPLETED') {
          const link = data.reportMeta?.s3Link?.signedLink
          if (data.status === 'SUCCESS' && link) {
            window.open(link, '_blank')
            enqueueSnackbar(`Forward Shipment MIS for ${rangeText} is ready`, { variant: 'success' })
          } else {
            enqueueSnackbar(data.errorMsg || 'Could not generate the Forward Shipment MIS. Please try again.', { variant: 'error' })
          }
          return
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
      }
      if (mounted.current) {
        enqueueSnackbar('The MIS is taking longer than usual. You will find it on the Reports page once it is ready.', { variant: 'warning' })
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || err.message || 'Could not download the MIS. Please try again.', { variant: 'error' })
    } finally {
      if (mounted.current) setDownloading(false)
    }
  }

  return (
    <Tooltip title={missingRange
      ? 'There is no date range to download yet. Apply a date range or wait for the list to load.'
      : `Downloads the Forward Shipment MIS (all order-level fields) for shipments created ${rangeText}. Other filters are not applied.`}
    >
      <span>
        <LoadingButton
          variant='outlined'
          size='small'
          sx={secondaryBtnSx}
          startIcon={<DownloadIcon />}
          loading={downloading}
          disabled={disabled || missingRange}
          onClick={download}
        >
          Download MIS
        </LoadingButton>
      </span>
    </Tooltip>
  )
}
